import { findChatById } from '@/lib/chatIndex';
import MessageReactions from './MessageReactions';
import React, { useMemo } from 'react';
import { StyleSheet, Platform, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import MessageActions from '@/components/Messages/MessageActions';
import MessageContent from '@/components/Messages/MessageContent';
import MessageButtons from '@/components/Messages/MessageButtons';
import useResponsiveness from '@/hooks/useResponsiveness';
import useNativeMessageActions from '@/hooks/useNativeMessageActions';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import MessageThinkingContent from '@/components/Messages/MessageThinkingContent';
import MessageExpandable from '@/components/Messages/MessageExpandable';
import MessageImages from '@/components/Messages/MessageImages';
import MessageFiles from '@/components/Messages/MessageFiles';
import { imagesNotCoveredByFiles } from '@/constants/utils/message_file_groups';
import MessageQuote from '@/components/Messages/MessageQuote';
import CreditsDepletedMessage from '@/components/Messages/CreditsDepletedMessage';
import PlusRequiredMessage from '@/components/Messages/PlusRequiredMessage';
import ContentModerationMessage from '@/components/Messages/ContentModerationMessage';
import { useAuthStore, useUIStore, useChatStore } from '@/store';
import PropTypes from 'prop-types';
import moment from 'moment-mini';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS, RESPONSE_ACTION_TYPES } from '@/constants/config';
import { isDarkTheme } from '@/constants/Colors';
import { getTemporaryUserBubbleStyle } from '@/constants/utils/temporaryChatStyles';
import MessageInferenceJob from '@/components/Messages/MessageInferenceJob';
import { getMessageDisplayInfo, computeImageAwareIsWaiting, shouldShowMessageTimestamp } from '@/constants/utils/message';
import { openUploadedChatFile, openMessageFile } from '@/constants/utils/files';
import {
  messageHasEditorInsertContent,
  messageHasMarkdownTable,
} from '@/constants/utils/message_editor_insert';
import { isCreditMessageError as isCreditError, isPlusMessageError as isPlusError } from '@/constants/utils/response_errors';

const formatMessageTime = (timestamp) => {
  const messageTime = moment(timestamp || Date.now());
  const now = moment();
  const diffDays = now.diff(messageTime, 'days');
  const time = messageTime.format('LT');
  if (diffDays === 0) return time;
  if (diffDays === 1) return `${time} · Yesterday`;
  if (diffDays <= 3) return `${time} · ${messageTime.format('ddd')}`;
  return `${time} · ${messageTime.format('D MMM')}`;
};

// Helper to check if error is a content moderation rejection
const isModerationError = (message) => {
  return message?.buttons?.some(btn => btn?.action === RESPONSE_ACTION_TYPES.CONTENT_MODERATION);
};

const createStyles = (colors, isUser, isXS, messageWidth, isInMessageGroup) => StyleSheet.create({
  container: {
    paddingVertical: 8,
    paddingHorizontal: isInMessageGroup ? 0 : 16,
    position: 'relative',
    ...(Platform.OS === 'web' && { cursor: 'default' }),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    ...(Platform.OS === 'web' && {
      ':hover': {
        backgroundColor: colors.solid_color_secondary,
      },
    }),
  },
  messageContainer: {
    flexDirection: 'row',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    width: '100%',
  },
  messageWrapper: {
    // User messages shrink-to-fit; assistant messages must be a definite-width
    // block so wide children (product carousels, code) cannot inflate the chat
    // column via shrink-to-fit / content min-width.
    ...(Platform.OS === 'web'
      ? (isUser
          ? { display: 'inline-block' }
          : { display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' })
      : (isUser
          ? { alignSelf: 'flex-end' }
          : { width: '100%', overflow: 'hidden' })),
    maxWidth: isUser ? (Platform.OS !== 'web' ? '92.5%' : (isXS ? '85%' : '80%')) : '100%',
    position: 'relative',
    height: 'auto',
    // Opacity only — height/padding/transform transitions cause scroll jumps
    // on mobile browsers when streaming ends and layout settles.
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease-out',
    }),
    paddingBottom: 0,
    minWidth: isUser ? 200 : 0,
    flexShrink: isUser ? 0 : 1,
  },
  messageActionsContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minWidth: isUser ? 150 : 250,
    height: 0,
    marginTop: 0,
    overflow: 'hidden',
    opacity: 0,
    ...(Platform.OS === 'web' ? {
      transition: 'opacity 0.3s ease-out',
      transform: 'translateY(-15px)',
      pointerEvents: 'none',
    } : {
      transform: [{ translateY: -15 }],
    }),
  },
  content: {
    flex: 1,
  },
  regularMessageContainer: {
    ...(Platform.OS === 'web' && { display: 'block', textAlign: isUser ? 'right' : 'left' }),
  },
  bubble: {
    backgroundColor: isUser ? colors.solid_color_user_bubble : 'transparent',
    paddingHorizontal: isUser ? 12 : 0,
    paddingVertical: isUser ? 8 : 0,
    borderRadius: isUser ? 20 : 0,
    maxWidth: '100%',
    minWidth: 0,
    // A shrink-to-fit assistant bubble can grow to the min-content width of
    // math/code, making all prose wrap beyond the clipped message boundary.
    // Keep user text left-aligned inside the right-positioned bubble.
    ...(Platform.OS === 'web' && (isUser
      ? { display: 'inline-block', textAlign: 'left' }
      : { display: 'block', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' })),
  },
  message: {
    fontSize: 14,
    color: isUser ? (colors.text_color_user_bubble || colors.text_color_primary) : colors.text_color_primary,
    lineHeight: 20,
  },
  errorContainer: {
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    width: '100%',
    backgroundColor: 'rgba(254, 0, 0, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(254, 0, 0, 0.25)',
    maxWidth: '100%',
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  errorContentWrapper: {
    marginTop: 5,
  },
  errorIcon: {
    color: '#DC2626',
    marginTop: 0,
    marginRight: 8,
    flexShrink: 0,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#991B1B',
    flex: 1,
  },
  messageContentWrapper: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    ...(Platform.OS === 'web' && { overflow: 'hidden' }),
  },
  preferenceButtonContainer: {
    marginTop: 6,
    alignItems: 'flex-start',
  },
});

// Memoized error message component
const ErrorMessage = React.memo(({ conversationId, message, styles, colors, isUser }) => (
  <View style={styles.errorContainer}>
    <View style={styles.errorContent}>
      <View style={styles.errorContentWrapper}>
        <Icon 
          name="circle-exclamation" 
          size={16} 
          color={colors.color_destructive}
          style={styles.errorIcon}
        />
      </View>
      <View style={styles.messageContentWrapper}>
        <MessageContent
          conversationId={conversationId}
          messageId={message.id}
          messageLayout={MESSAGE_LAYOUT.WIDE}
          isUser={isUser}
          content={message.content} 
          citations={message.citations}
          products={message.products}
          stockQuotes={message.stockQuotes}
          places={message.places}
          map={message.map}
          textStyle={styles.errorText}
          isError={message.isError}
          hasError={message.hasError}
          isDeleted={message?.flags?.isDeleted}
        />
      </View>
    </View>
  </View>
));

// Memoized regular message component
const RegularMessage = React.memo(({ 
  conversationId, 
  message, 
  styles, 
  isUser, 
  onImageClick,
}) => {
  const { colors, theme } = useTheme();
  const isTemporaryChat = useChatStore((state) => (
    findChatById(state.chats, conversationId)?.flags?.isTemporary === true
  ));
  const temporaryBubbleStyle = useMemo(
    () => (isUser && isTemporaryChat ? getTemporaryUserBubbleStyle(theme) : null),
    [isUser, isTemporaryChat, theme],
  );

  const { shouldDisplayMessage, hasAnyInferencesWithIncompleteJobs, badStatuses } = getMessageDisplayInfo(message, isUser, { ignoreFiles: true });

  if (!shouldDisplayMessage) return null;

  const contentNode = (
    <MessageContent
      conversationId={conversationId}
      messageId={message.id}
      content={message.content}
      citations={message.citations}
      products={message.products}
      stockQuotes={message.stockQuotes}
      places={message.places}
      map={message.map}
      messageLayout={MESSAGE_LAYOUT.WIDE}
      isUser={isUser}
      isStreaming={message.isStreaming}
      isWaiting={computeImageAwareIsWaiting(message)}
      isTyping={message.isTyping}
      isRequestingMedia={message.isRequestingMedia}
      modelIds={message.modelIds}
      agentIds={message.agentIds}
      isDeleted={message?.flags?.isDeleted}
      inferenceStatus={message.inferenceStatus}
      inferenceStage={message.inferenceStage}
      inferenceElapsedMs={message.inferenceElapsedMs}
    />
  );

  return (
    <View style={styles.regularMessageContainer}>
      <View style={[styles.bubble, temporaryBubbleStyle]}>
        {!!message.thinkingContent && !message.isThinking && !message.isStreaming && !message.isWaiting && !message.isRequestingMedia && (
          <MessageThinkingContent
            conversationId={conversationId}
            messageId={message.id}
            thinkingContent={message.thinkingContent}
            thinkingDurationMs={message.thinkingDurationMs}
            thinkingStartTime={message.thinkingStartTime}
            isThinking={message.isThinking}
            inferenceStatus={message.inferenceStatus}
            inferenceStage={message.inferenceStage}
            messageLayout={MESSAGE_LAYOUT.WIDE}
          />
        )}
        {isUser ? (
          <MessageExpandable
            content={message.content}
            fadeColor={colors.solid_color_user_bubble}
            align="left"
          >
            {contentNode}
          </MessageExpandable>
        ) : (
          contentNode
        )}
        {hasAnyInferencesWithIncompleteJobs && (
          <View style={{ marginTop: 8 }}>
            {message.inferences.map((inf) => (inf?.output?.jobs || [])
              .filter((job) => badStatuses.has(job?.status))
              .map((job) => (
                <MessageInferenceJob
                  key={`${inf.id || 'inf'}-${job.id || Math.random()}`}
                  conversationId={conversationId}
                  messageId={message.id}
                  inferenceId={inf.id}
                  job={job}
                />
              ))
            )}
          </View>
        )}
        {message.images && (
          <MessageImages
            conversationId={conversationId}
            images={imagesNotCoveredByFiles(message.images, message.files)}
            onImageClick={onImageClick}
          />
        )}
      </View>
    </View>
  );
});

// Memoized message content component
const MessageContentWrapper = React.memo(({
  conversationId,
  message, 
  styles, 
  colors,
  isUser,
  showTimestamps,
  onImageClick,
  onButtonClick,
  onOpenLightbox,
  onOpenFileEditor,
}) => (
  <View style={styles.content}>
    {/* Check if this is a credit-related error (show friendly message instead of error UI) */}
    {(message.isError || message.hasError) && isCreditError(message) ? (
      <CreditsDepletedMessage message={message} />
    ) : (message.isError || message.hasError) && isPlusError(message) ? (
      <PlusRequiredMessage message={message} />
    ) : (message.isError || message.hasError) && isModerationError(message) ? (
      <ContentModerationMessage message={message} />
    ) : message.isError || message.hasError || message?.flags?.isDeleted ? (
      <ErrorMessage conversationId={conversationId} message={message} styles={styles} colors={colors} isUser={isUser} />
    ) : (
      <>
        {isUser && !!message.quoteContent && (
          <MessageQuote quoteContent={message.quoteContent} />
        )}
        {/* User uploads stay above the reply; assistant artifacts render below
            the acknowledgment so auto-scroll-to-end keeps the card on screen. */}
        {isUser && Array.isArray(message.files) && message.files.length > 0 && (
          <MessageFiles
            files={message.files}
            isSmall={isUser}
            align={isUser ? 'right' : 'left'}
            isEditable={!isUser}
            onEdit={(file) => {
              if (file && file?.cdnPath && !isUser) {
                openMessageFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: true,
                  withInpainting: true,
                  withChatInput: true,
                });
              } else if (file && file?.id && isUser) {
                // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
                // web; everything else keeps the file details modal.
                if (!openUploadedChatFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                })) onOpenFileEditor(file.id);
              } else {
                console.log('File pressed but do nothing:', file);
              }
            }}
            onPress={(file) => {
              if (file && file?.cdnPath && !isUser) {
                openMessageFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                });
              } else if (file && file?.id && isUser) {
                // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
                // web; everything else keeps the file details modal.
                if (!openUploadedChatFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                })) onOpenFileEditor(file.id);
              } else {
                console.log('File pressed but do nothing:', file);
              }
            }}
          />
        )}
        <RegularMessage
          conversationId={conversationId}
          message={message}
          styles={styles}
          isUser={isUser}
          onImageClick={onImageClick}
        />
        {!isUser && Array.isArray(message.files) && message.files.length > 0 && (
          <MessageFiles
            files={message.files}
            isSmall={isUser}
            align={isUser ? 'right' : 'left'}
            isEditable={!isUser}
            onEdit={(file) => {
              if (file && file?.cdnPath && !isUser) {
                openMessageFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: true,
                  withInpainting: true,
                  withChatInput: true,
                });
              } else if (file && file?.id && isUser) {
                if (!openUploadedChatFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                })) onOpenFileEditor(file.id);
              } else {
                console.log('File pressed but do nothing:', file);
              }
            }}
            onPress={(file) => {
              if (file && file?.cdnPath && !isUser) {
                openMessageFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                });
              } else if (file && file?.id && isUser) {
                if (!openUploadedChatFile(file, {
                  conversationId,
                  onOpenLightbox,
                  openToEditor: false,
                  withInpainting: true,
                  withChatInput: true,
                })) onOpenFileEditor(file.id);
              } else {
                console.log('File pressed but do nothing:', file);
              }
            }}
          />
        )}
        {isUser && message.isEdited && (
          <Text size="xs" color="secondary" style={{ opacity: 0.6, marginTop: 2 }}>
            (edited)
          </Text>
        )}
      </>
    )}
    {isUser && <MessageReactions conversationId={conversationId} message={message} />}
    {shouldShowMessageTimestamp(message, { isUser, showTimestamps }) && (
      <Text size="xs" style={{ opacity: 0.5, marginTop: 4, textAlign: isUser ? 'right' : 'left' }}>
        {formatMessageTime(message.timestamp)}
      </Text>
    )}
    {/* Don't show MessageButtons for credit/Plus/moderation errors since their message components handle the CTA */}
    {!((message.isError || message.hasError) && (isCreditError(message) || isPlusError(message) || isModerationError(message))) && (
      <MessageButtons conversationId={conversationId} messageId={message.id} buttons={message.buttons} onButtonClick={onButtonClick} />
    )}
  </View>
), (prevProps, nextProps) => {
  if (prevProps.colors !== nextProps.colors || prevProps.styles !== nextProps.styles) return false;
  if (prevProps.showTimestamps !== nextProps.showTimestamps) return false;
  const prev = prevProps.message;
  const next = nextProps.message;
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prev.isError === next.isError &&
    prev.hasError === next.hasError &&
    prev.errorType === next.errorType &&
    prev.reactions?.agent === next.reactions?.agent &&
    prev.content === next.content &&
    prev.thinkingContent === next.thinkingContent &&
    prev.thinkingDurationMs === next.thinkingDurationMs &&
    prev.thinkingStartTime === next.thinkingStartTime &&
    prev.inferenceStatus === next.inferenceStatus &&
    prev.inferenceStage === next.inferenceStage &&
    prev.inferenceElapsedMs === next.inferenceElapsedMs &&
    prev.isThinking === next.isThinking &&
    prev.isStreaming === next.isStreaming &&
    prev.isWaiting === next.isWaiting &&
    prev.isTyping === next.isTyping &&
    prev.isRequestingMedia === next.isRequestingMedia &&
    prev.isEdited === next.isEdited &&
    prev.flags?.isDeleted === next.flags?.isDeleted &&
    prev.flags?.isGroupedResponsePreferred === next.flags?.isGroupedResponsePreferred &&
    // Arrays are replaced wholesale by store updates (never mutated in
    // place) — reference equality is sufficient (see Message.jsx).
    prev.images === next.images &&
    prev.buttons === next.buttons &&
    prev.citations === next.citations &&
    prev.products === next.products &&
    prev.stockQuotes === next.stockQuotes &&
    prev.places === next.places &&
    prev.map === next.map &&
    prev.modelIds === next.modelIds &&
    prev.agentIds === next.agentIds &&
    prev.inferences === next.inferences &&
    prev.files === next.files
  );
});

// Memoized mobile message component
const MobileMessage = React.memo(({ 
  conversationId,
  message,
  isUser,
  isLastMessage,
  styles,
  colors,
  theme,
  showActions,
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onImageClick,
  onButtonClick,
  onOpenLightbox,
  onOpenFileEditor,
  userName,
  userAvatar,
  showPreferenceButton,
  showPreferenceIcons,
  onPreferResponse,
  onRejectResponse,
  isInMessageGroup,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
}) => {
  const { MessageContainer, actionsVisible, hoverProps } = useNativeMessageActions();

  return (
  <MessageContainer
    accessible={false}
    style={styles.container}
    {...hoverProps}
  >
    <View style={styles.messageContainer}>
      {message.role === 'assistant' ? botAvatar : null}
      <View style={styles.messageWrapper}>
        <MessageContentWrapper
          conversationId={conversationId}
          messageId={message.id}
          message={message}
          styles={styles}
          colors={colors}
          isUser={isUser}
          showTimestamps={showTimestamps}
          onImageClick={onImageClick}
          onButtonClick={onButtonClick}
          onOpenLightbox={onOpenLightbox}
          onOpenFileEditor={onOpenFileEditor}
        />
        {showActions && (
          <View pointerEvents={actionsVisible ? 'auto' : 'none'} style={{
            marginTop: 4,
            height: 34,
            opacity: actionsVisible ? 1 : 0,
            overflow: 'hidden',
          }}>
            <MessageActions
              conversationId={conversationId}
              message={message}
              isUser={isUser}
              userName={userName}
              userAvatar={userAvatar}
              isVisible={actionsVisible}
              isCompact={true}
              onCopy={showCopy ? onCopy : undefined}
              onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
              onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
              onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
              onShare={onShare}
              onSpeak={showCopy ? onSpeak : undefined}
              onRegenerate={isInMessageGroup ? null : onRegenerate}
              onFork={onFork}
              onStartNewTask={onStartNewTask}
              onBookmark={onBookmark}
              onFeedback={onFeedback}
              onEdit={onEdit}
              onReport={onReport}
              onExport={onExport}
              onDelete={onDelete}
              showAttribution={true}
              workActions={message.workActions}
              isLastMessage={isLastMessage}
              modelIds={displayModelIds || message.modelIds}
              agentIds={message.agentIds}
              isAutoMode={isAutoMode}
              inferences={message.inferences}
              isImage={Array.isArray(message.images) && message.images.length > 0}
              isFile={Array.isArray(message.files) && message.files.length > 0}
              isBookmarked={message?.flags?.isSaved === true}
              parameters={message.parameters}
              createdAt={message.timestamp}
              showPreferenceIcons={showPreferenceIcons}
              isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
              onPreferResponse={onPreferResponse}
              onRejectResponse={onRejectResponse}
            />
          </View>
        )}
        {!isUser && showPreferenceButton && onPreferResponse && (
          <View style={styles.preferenceButtonContainer}>
            <Button
              text="I prefer this response"
              variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
              size="small"
              onPress={onPreferResponse}
            />
          </View>
        )}
      </View>
    </View>
  </MessageContainer>
  );
});

// Memoized desktop message component
const DesktopMessage = React.memo(({
  conversationId,
  message,
  isUser,
  styles,
  colors,
  theme,
  showActions,
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onExport,
  onReport,
  onDelete,
  onImageClick,
  onButtonClick,
  onOpenLightbox,
  onOpenFileEditor,
  userName,
  userAvatar,
  showPreferenceButton,
  showPreferenceIcons,
  onPreferResponse,
  onRejectResponse,
  isInMessageGroup,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
}) => (
  <View style={styles.container}>
    <View style={styles.messageContainer}>
      {message.role === 'assistant' ? botAvatar : null}
      <div 
        style={styles.messageWrapper}
        className={`message-bubble-wrapper ${(message.isError || message?.flags?.isDeleted) ? 'error' : ''}`}
        data-is-user={isUser ? 'true' : 'false'}
        data-message-id={message.id}
        data-is-last-message={isLastMessage ? 'true' : 'false'}
        data-is-animated="true"
        tabIndex={0}
      >
        <MessageContentWrapper
          conversationId={conversationId}
          messageId={message.id}
          message={message}
          styles={styles}
          colors={colors}
          isUser={isUser}
          showTimestamps={showTimestamps}
          onImageClick={onImageClick}
          onButtonClick={onButtonClick}
          onOpenLightbox={onOpenLightbox}
          onOpenFileEditor={onOpenFileEditor}
        />
        {showActions && (
          <div 
            style={styles.messageActionsContainer} 
            className="message-bubble-actions-container"
          >
            <MessageActions
              conversationId={conversationId}
              message={message}
              isUser={isUser}
              userName={userName}
              userAvatar={userAvatar}
              isVisible={true}
              isCompact={true}
              onCopy={showCopy ? onCopy : undefined}
              onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
              onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
              onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
              onShare={onShare}
              onSpeak={showCopy ? onSpeak : undefined}
            onRegenerate={isInMessageGroup ? null : onRegenerate}
              onFork={onFork}
              onStartNewTask={onStartNewTask}
              onBookmark={onBookmark}
              onFeedback={onFeedback}
              onEdit={onEdit}
              onReport={onReport}
              onDelete={onDelete}
              onExport={onExport}
              showAttribution={true}
              workActions={message.workActions}
              isLastMessage={isLastMessage}
              modelIds={displayModelIds || message.modelIds}
              agentIds={message.agentIds}
              isAutoMode={isAutoMode}
              inferences={message.inferences}
              isImage={Array.isArray(message.images) && message.images.length > 0}
              isFile={Array.isArray(message.files) && message.files.length > 0}
              isBookmarked={message?.flags?.isSaved === true}
              parameters={message.parameters}
              createdAt={message.timestamp}
            showPreferenceIcons={showPreferenceIcons}
            isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
            onPreferResponse={onPreferResponse}
            onRejectResponse={onRejectResponse}
            />
          </div>
        )}
        {showPreferenceButton && onPreferResponse && (
          <View style={styles.preferenceButtonContainer}>
            <Button
              text="I prefer this response"
              variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
              size="small"
              onPress={onPreferResponse}
            />
          </View>
        )}
      </div>
    </View>
  </View>
));

const MessageWide = ({ 
  conversationId,
  message, 
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onDelete,
  onExport,
  onReport,
  onButtonClick,
  onImageClick,
  messageWidth,
  onOpenLightbox,
  onOpenFileEditor,
  isInMessageGroup,
  isLastGroupInChat,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
  onPreferResponse,
  onRejectResponse,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isMobile = Platform.OS !== 'web';
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const userName = useAuthStore(state => state.user?.fullName) || 'You';
  const userAvatar = useAuthStore(state => state.user?.avatarPath) || null;
  const showTimestamps = useUIStore(state => state.showTimestamps);

  const isUser = message.role === 'user';
  const isAuthenticated = token && !isTokenExpired();
  const isStreaming = message.isStreaming;
  const showActions = isAuthenticated && !message.isError && !message.isTyping && !isStreaming && !message.isThinking && !message?.flags?.isDeleted;
  const showCopy = message.content && message.content.length > 0;
  const showAddToEditor = showCopy || messageHasEditorInsertContent(message);
  const showAddToDocument = !isUser && showAddToEditor;
  const showAddToSpreadsheet = !isUser && messageHasMarkdownTable(message);

  const styles = useMemo(() => 
    createStyles(colors, isUser, isXS, messageWidth, isInMessageGroup),
    [colors, isUser, isXS, messageWidth, isInMessageGroup]
  );

  // Determine whether to show preference button (last group) or icons (not last group)
  // Check if a preference has been locally selected for this group
  const hasSelectedPreference = message.flags?.hasSelectedPreferenceLocally;
  // Show button only on last group AND if no preference has been selected locally yet
  const showPreferenceButton = isInMessageGroup && isLastGroupInChat && !isUser && message.content && !hasSelectedPreference;
  // Show icons if not last group, OR if last group but preference has been selected
  const showPreferenceIcons = isInMessageGroup && !isUser && message.content && (!isLastGroupInChat || hasSelectedPreference);

  if (isMobile) {
    return (
      <MobileMessage
        conversationId={conversationId}
        message={message}
        isUser={isUser}
        isLastMessage={isLastMessage}
        styles={styles}
        colors={colors}
        theme={theme}
        showActions={showActions}
        showCopy={showCopy}
        showAddToEditor={showAddToEditor}
        showAddToDocument={showAddToDocument}
        showAddToSpreadsheet={showAddToSpreadsheet}
        showTimestamps={showTimestamps}
        onCopy={onCopy}
        onAddToEditor={onAddToEditor}
        onAddToDocument={onAddToDocument}
        onAddToSpreadsheet={onAddToSpreadsheet}
        onShare={onShare}
        onSpeak={onSpeak}
        onRegenerate={onRegenerate}
        onFork={onFork}
              onStartNewTask={onStartNewTask}
        onBookmark={onBookmark}
        onFeedback={onFeedback}
        onEdit={onEdit}
        onReport={onReport}
        onDelete={onDelete}
        onExport={onExport}
        onImageClick={onImageClick}
        onButtonClick={onButtonClick}
        onOpenLightbox={onOpenLightbox}
        onOpenFileEditor={onOpenFileEditor}
        userName={userName}
        userAvatar={userAvatar}
        showPreferenceButton={showPreferenceButton}
        showPreferenceIcons={showPreferenceIcons}
        onPreferResponse={onPreferResponse}
        onRejectResponse={onRejectResponse}
        isInMessageGroup={isInMessageGroup}
        isAutoMode={isAutoMode}
        displayModelIds={displayModelIds}
        botAvatar={botAvatar}
      />
    );
  }

  return (
    <DesktopMessage
      conversationId={conversationId}
      message={message}
      isUser={isUser}
      styles={styles}
      colors={colors}
      theme={theme}
      showActions={showActions}
      showCopy={showCopy}
      showAddToEditor={showAddToEditor}
      showAddToDocument={showAddToDocument}
      showAddToSpreadsheet={showAddToSpreadsheet}
      showTimestamps={showTimestamps}
      isLastMessage={isLastMessage}
      onCopy={onCopy}
      onAddToEditor={onAddToEditor}
      onAddToDocument={onAddToDocument}
      onAddToSpreadsheet={onAddToSpreadsheet}
      onShare={onShare}
      onSpeak={onSpeak}
      onRegenerate={onRegenerate}
      onFork={onFork}
              onStartNewTask={onStartNewTask}
      onBookmark={onBookmark}
      onFeedback={onFeedback}
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={onReport}
      onExport={onExport}
      onImageClick={onImageClick}
      onButtonClick={onButtonClick}
      onOpenLightbox={onOpenLightbox}
      onOpenFileEditor={onOpenFileEditor}
      userName={userName}
      userAvatar={userAvatar}
      showPreferenceButton={showPreferenceButton}
      showPreferenceIcons={showPreferenceIcons}
      onPreferResponse={onPreferResponse}
      onRejectResponse={onRejectResponse}
      isInMessageGroup={isInMessageGroup}
      isAutoMode={isAutoMode}
      displayModelIds={displayModelIds}
      botAvatar={botAvatar}
    />
  );
};

MessageWide.propTypes = {
  conversationId: PropTypes.string.isRequired,
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    isError: PropTypes.bool,
    hasError: PropTypes.bool,
    isTyping: PropTypes.bool,
    isStreaming: PropTypes.bool,
    isThinking: PropTypes.bool,
    isWaiting: PropTypes.bool,
    isRequestingMedia: PropTypes.string, // image, video, audio, pdf, etc
    thinkingContent: PropTypes.string,
    thinkingDurationMs: PropTypes.number,
    images: PropTypes.array,
    buttons: PropTypes.array,
    modelIds: PropTypes.array,
    agentIds: PropTypes.array,
  }).isRequired,
  isLastMessage: PropTypes.bool,
  onCopy: PropTypes.func,
  onAddToEditor: PropTypes.func,
  onAddToDocument: PropTypes.func,
  onAddToSpreadsheet: PropTypes.func,
  onShare: PropTypes.func,
  onSpeak: PropTypes.func,
  onRegenerate: PropTypes.func,
  onFork: PropTypes.func,
  onStartNewTask: PropTypes.func,
  onBookmark: PropTypes.func,
  onFeedback: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onExport: PropTypes.func,
  onReport: PropTypes.func,
  onButtonClick: PropTypes.func,
  onImageClick: PropTypes.func,
  isInMessageGroup: PropTypes.bool,
  isLastGroupInChat: PropTypes.bool,
  isAutoMode: PropTypes.bool,
  displayModelIds: PropTypes.arrayOf(PropTypes.string),
  onPreferResponse: PropTypes.func,
  onRejectResponse: PropTypes.func,
};

export default React.memo(MessageWide);
